import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  Avatar,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  Stack,
  Tabs,
  Tab
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Security as SecurityIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  AdminPanelSettings as AdminIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { cs } from 'date-fns/locale';
import api from '../../config/axios';

import { RootState, AppDispatch } from '../../store/store';
import useModernAlert from '../../hooks/useModernAlert';
import { getProfile } from '../../store/slices/authSlice';
import BusinessUnits from '../BusinessUnits/BusinessUnits';

const roleLabels = {
  admin: 'Administrátor',
  user: 'Uživatel',
  superadmin: 'Super Administrátor'
} as const;

const Profile: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { showError, showSuccess, showConfirm, AlertComponent } = useModernAlert();
  const { user } = useSelector((state: RootState) => state.auth);

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: user?.firstName || ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // Tab management
  const [activeTab, setActiveTab] = useState(0);

  // Fetch user profile when component mounts
  useEffect(() => {
    if (!user) {
      dispatch(getProfile());
    }
  }, [dispatch, user]);

  // Update formData when user data changes
  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || ''
      });
    }
  }, [user]);







  const handleSaveProfile = async () => {
    try {
      const response = await api.put('/auth/profile', {
        firstName: formData.firstName,
        lastName: user?.lastName || '' // Use existing lastName since it's not editable
      });

      if (response.status === 200) {
        setMessage({ type: 'success', text: 'Profil byl úspěšně aktualizován' });
        setIsEditing(false);
        
        // Refresh the user profile data from the server
        dispatch(getProfile());
      } else {
        throw new Error('Update failed');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      setMessage({ type: 'error', text: 'Chyba při aktualizaci profilu' });
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'Nová hesla se neshodují' });
      return;
    }

    try {
      const response = await api.post('/auth/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      if (response.status === 200) {
        setMessage({ type: 'success', text: 'Heslo bylo úspěšně změněno' });
        setShowPasswordForm(false);
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        throw new Error('Password change failed');
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Chyba při změně hesla' });
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      firstName: user?.firstName || ''
    });
  };

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Uživatel není přihlášen</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Typography 
        variant={isMobile ? "h5" : "h4"} 
        component="h1" 
        gutterBottom
        sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
      >
        Můj profil
      </Typography>

      {message && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 3 }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
        >
          <Tab 
            label="Základní informace" 
            icon={<PersonIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Správa účetních jednotek" 
            icon={<BusinessIcon />} 
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Grid container spacing={{ xs: 2, md: 3 }}>
        {/* Profile Information */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                Základní informace
              </Typography>
              {!isEditing ? (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setIsEditing(true)}
                >
                  Upravit
                </Button>
              ) : (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveProfile}
                  >
                    Uložit
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancel}
                  >
                    Zrušit
                  </Button>
                </Box>
              )}
            </Box>

            {/* Information about restricted fields */}
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Poznámka:</strong> Email a role uživatele nelze změnit z bezpečnostních důvodů. 
                Pro změnu těchto údajů kontaktujte administrátora systému.
              </Typography>
            </Alert>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Jméno"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  disabled={!isEditing}
                  margin="normal"
                  InputProps={{
                    startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={user.email}
                  disabled={true}
                  margin="normal"
                  InputProps={{
                    startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    endAdornment: (
                      <Chip 
                        label="Neměnitelné" 
                        size="small" 
                        color="warning" 
                        variant="outlined"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    )
                  }}
                  helperText="Email lze změnit pouze přes administrátora"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={user.role}
                    disabled={true}
                    label="Role"
                    startAdornment={user.role === 'admin' || user.role === 'superadmin' ? <AdminIcon sx={{ mr: 1, color: 'text.secondary' }} /> : <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />}
                    endAdornment={
                      <Chip 
                        label="Neměnitelné" 
                        size="small" 
                        color="warning" 
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', mr: 1 }}
                      />
                    }
                  >
                    {Object.entries(roleLabels).map(([key, label]) => (
                      <MenuItem key={key} value={key}>{label}</MenuItem>
                    ))}
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Role lze změnit pouze přes administrátora
                  </Typography>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Člen od"
                  value={user.createdAt && !isNaN(new Date(user.createdAt).getTime()) ? format(new Date(user.createdAt), 'dd.MM.yyyy', { locale: cs }) : 'N/A'}
                  disabled
                  margin="normal"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Profile Summary */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar
                sx={{ 
                  width: 80, 
                  height: 80, 
                  mx: 'auto', 
                  mb: 2,
                  bgcolor: user.role === 'admin' ? 'error.main' : user.role === 'superadmin' ? 'warning.main' : 'primary.main'
                }}
              >
                {user.firstName?.charAt(0).toUpperCase()}
              </Avatar>
              <Typography variant="h6" gutterBottom>
                                  {user.firstName}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {user.email}
              </Typography>
              <Chip
                label={roleLabels[user.role as keyof typeof roleLabels] || user.role}
                color={user.role === 'admin' ? 'error' : user.role === 'superadmin' ? 'warning' : 'primary'}
                icon={user.role === 'admin' ? <AdminIcon /> : user.role === 'superadmin' ? <AdminIcon /> : <PersonIcon />}
                sx={{ mt: 1 }}
              />
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="textSecondary">
                Poslední přihlášení:
              </Typography>
              <Typography variant="body2">
                {user.lastLogin && !isNaN(new Date(user.lastLogin).getTime())
                  ? format(new Date(user.lastLogin), 'dd.MM.yyyy HH:mm', { locale: cs })
                  : 'Nikdy'
                }
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Password Change */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                Změna hesla
              </Typography>
              <Button
                variant="outlined"
                startIcon={<SecurityIcon />}
                onClick={() => setShowPasswordForm(!showPasswordForm)}
              >
                {showPasswordForm ? 'Skrýt' : 'Změnit heslo'}
              </Button>
            </Box>

            {showPasswordForm && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Současné heslo"
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Nové heslo"
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Potvrdit nové heslo"
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      onClick={handleChangePassword}
                      disabled={!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                    >
                      Změnit heslo
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setShowPasswordForm(false);
                        setPasswordData({
                          currentPassword: '',
                          newPassword: '',
                          confirmPassword: ''
                        });
                      }}
                    >
                      Zrušit
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Paper>
        </Grid>





        {/* Activity Summary */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Aktivita
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="primary.main">
                      0
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Zpracovaných faktur
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="secondary.main">
                      0
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Vytvořených ABO souborů
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main">
                      0 Kč
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Celková hodnota
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="warning.main">
                      0
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Dní v systému
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      )}

      {/* Business Units Tab */}
      {activeTab === 1 && (
        <BusinessUnits />
      )}

      {AlertComponent}
    </Box>
  );
};

export default Profile; 